/**
 * Requests, decisions and projects — `modules/work_orders/routes.ts`.
 *
 * `SERVICES.md` §Projects: client approval in the portal **is** the contract event,
 * so `useApproveRequest` is the one money mutation on this page and it carries the
 * `Idempotency-Key` the route requires. Declining, commenting and attaching are
 * not money and carry none.
 *
 * The attachment upload is deliberately not a `useApiMutation`: the body is the
 * bytes, the name rides in `X-File-Name` and the media type is the file's own
 * (`modules/work_orders/routes.ts`), so it goes through `api.upload` — which reports
 * the bytes as they go in a browser, and is the fake `fetch` under a test.
 */

import {
  MAX_PAGE_LIMIT,
  type DraftWorkOrderInput,
  type UpdateWorkOrderInput,
} from '@fareast/shared'
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'

import { useApi } from '../provider.js'
import { useApiMutation, useApiQuery, type ApiError } from '../hooks.js'
import { moduleKey, queryKeys } from '../keys.js'
import { NetworkError, problemOf } from '../problem.js'
import type { UploadProgress } from '../upload.js'

/** How far back the ledger reaches. `'all'` is the "full request ledger" link. */
export type RequestWindow = number | 'all'

/**
 * What a detail read takes. `inline` is the opt-out `api/hooks.ts` documents: the
 * failure is handed back on the result instead of thrown at the route boundary, for
 * a panel that would rather say "we could not load this" than take the page down.
 */
export type DetailOptions = { readonly enabled?: boolean; readonly inline?: boolean }

function detail(options: DetailOptions) {
  return {
    ...(options.enabled === undefined ? {} : { enabled: options.enabled }),
    ...(options.inline === undefined ? {} : { inline: options.inline }),
  }
}

/**
 * `GET /api/clients/me/requests?window=` — the ledger.
 *
 * **The limit is sent, and it is not decoration.** `CursorPageQuerySchema` defaults
 * to 25 and Casaturca's ledger is longer, so the default cut two requests off the
 * end — including one the page's own "three more, delivered or billed" fold claims
 * to list, and one named in the prose above it. `MAX_PAGE_LIMIT` is 100, which is
 * every request any client in this world has raised; a client who passes it gets a
 * `next_cursor` and `RequestLedger` says so rather than quietly ending.
 */
export function useRequests(window?: RequestWindow) {
  const value = window === undefined ? undefined : String(window)
  const limit = String(MAX_PAGE_LIMIT)
  return useApiQuery({
    key: queryKeys['work-orders'].requests(value),
    request: (api) =>
      api.rest.clients.me.requests.$get({
        query: value === undefined ? { limit } : { window: value, limit },
      }),
  })
}

/**
 * `GET /api/clients/me/requests/:ref` — one request, with its lines and comments.
 *
 * `inline` matters here more than on a list read: a page that opens four decision
 * cards makes four of these, and one of them 404ing — a reference the ledger has and
 * the detail route does not — should leave three cards on screen rather than
 * replacing the page with an error boundary.
 */
export function useRequest(ref: string, options: DetailOptions = {}) {
  return useApiQuery({
    key: queryKeys['work-orders'].byRef(ref),
    request: (api) => api.rest.clients.me.requests[':ref'].$get({ param: { ref } }),
    ...detail(options),
  })
}

/** `POST /api/clients/me/requests` — the composer. A client raises an extra. */
export function useDraftRequest() {
  return useApiMutation({
    module: 'work-orders',
    request: (api, body: Omit<DraftWorkOrderInput, 'kind'>) =>
      api.rest.clients.me.requests.$post({ json: body }),
    alsoInvalidates: ['core'],
  })
}

/** `PATCH /api/clients/me/requests/:ref` — editing a draft the studio has not priced. */
export function useUpdateRequest() {
  return useApiMutation({
    module: 'work-orders',
    request: (api, variables: { ref: string } & UpdateWorkOrderInput) => {
      const { ref, ...body } = variables
      return api.rest.clients.me.requests[':ref'].$patch({ param: { ref }, json: body })
    },
    alsoInvalidates: ['core'],
  })
}

/** `POST /api/clients/me/requests/:ref/withdraw` — drop a draft before it is priced. */
export function useWithdrawDraft() {
  return useApiMutation({
    module: 'work-orders',
    request: (api, ref: string) =>
      api.rest.clients.me.requests[':ref'].withdraw.$post({ param: { ref } }),
    alsoInvalidates: ['core'],
  })
}

/** `DELETE /api/clients/me/requests/:ref/attachments/:fileId` — unlink from a draft. */
export function useDetachAttachment() {
  return useApiMutation({
    module: 'work-orders',
    request: (api, variables: { ref: string; fileId: string }) =>
      api.rest.clients.me.requests[':ref'].attachments[':fileId'].$delete({
        param: { ref: variables.ref, fileId: variables.fileId },
      }),
    alsoInvalidates: ['core'],
  })
}

/**
 * `POST /api/clients/me/requests/:ref/approve` — the contract event.
 *
 * `money: true` for both halves of what that flag means: the header, and the refusal
 * under impersonation. Staff never approve on a client's behalf — the studio's path
 * is `work_orders_record_offline_approval!` with the consent file (`CLAUDE.md`
 * §Work orders).
 */
export function useApproveRequest() {
  return useApiMutation({
    module: 'work-orders',
    money: true,
    request: (api, ref: string, options) =>
      api.rest.clients.me.requests[':ref'].approve.$post(
        { param: { ref } },
        { headers: options.headers },
      ),
    alsoInvalidates: ['core', 'billing', 'retainers'],
  })
}

/** `POST /api/clients/me/requests/:ref/decline` — nothing was started. */
export function useDeclineRequest() {
  return useApiMutation({
    module: 'work-orders',
    request: (api, variables: { ref: string; note: string }) =>
      api.rest.clients.me.requests[':ref'].decline.$post({
        param: { ref: variables.ref },
        json: { note: variables.note },
      }),
    alsoInvalidates: ['core'],
  })
}

/** `POST /api/clients/me/requests/:ref/comments` — a question on a request. */
export function useCommentOnRequest() {
  return useApiMutation({
    module: 'work-orders',
    request: (api, variables: { ref: string; body: string }) =>
      api.rest.clients.me.requests[':ref'].comments.$post({
        param: { ref: variables.ref },
        json: { body: variables.body },
      }),
  })
}

/** `GET /api/clients/me/projects` — each project with its milestone schedule. */
export function useProjects() {
  return useApiQuery({
    key: queryKeys['work-orders'].projects(),
    request: (api) => api.rest.clients.me.projects.$get({ query: {} }),
  })
}

/** `GET /api/clients/me/projects/:ref` — the quote fold under the milestone table. */
export function useProject(ref: string, options: DetailOptions = {}) {
  return useApiQuery({
    key: queryKeys['work-orders'].projectByRef(ref),
    request: (api) => api.rest.clients.me.projects[':ref'].$get({ param: { ref } }),
    ...detail(options),
  })
}

/** What an attachment upload is given. */
export type AttachmentUpload = {
  readonly ref: string
  readonly file: File
  /** Hears the bytes as they go — the composer's progress bar. */
  readonly onProgress?: UploadProgress | undefined
  /** Calls it off: the composer, when the account it was raised on is left. */
  readonly signal?: AbortSignal | undefined
}

/**
 * `POST /api/clients/me/requests/:ref/attachments` — the one non-JSON path.
 *
 * Written out rather than squeezed into `useApiMutation`, because that hook's whole
 * job is `json` in and `json` out and this sends neither: the body is the `File`
 * itself, `Content-Type` is the file's own media type and the name is a header
 * (`api/upload.ts` `fileHeaders`). What it keeps is the three things a page depends on
 * — a non-2xx answer is an `ApiProblem`, no answer at all is a `NetworkError`, and
 * success invalidates the module.
 */
export function useUploadAttachment(): UseMutationResult<unknown, ApiError, AttachmentUpload> {
  const api = useApi()
  const queryClient = useQueryClient()

  return useMutation<unknown, ApiError, AttachmentUpload>({
    mutationFn: async ({ ref, file, onProgress, signal }) => {
      let response: Response
      try {
        response = await api.upload({
          path: `/api/clients/me/requests/${encodeURIComponent(ref)}/attachments`,
          file,
          onProgress,
          signal,
        })
      } catch (error) {
        throw new NetworkError(error)
      }
      if (!response.ok) throw await problemOf(response)
      const body: unknown = await response.json()
      await queryClient.invalidateQueries({ queryKey: moduleKey('work-orders') })
      await queryClient.invalidateQueries({ queryKey: moduleKey('files') })
      return body
    },
  })
}
